import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Group } from "types";
import { PatientOption, Member } from "./types/types";
import { Select, Animate, Button } from "components";
import { capitalizeFirstLetter, pluralize } from "utils/helpers";
import "./AddMemberForm.styles.scss";
import AddExistingSelector from "./components/AddExistingSelector/AddExistingSelector";
import InviteNewInput from "./components/InviteNewInput/InviteNewInput";
import { formatSubmitData } from "./utils/utils";
import {
  useAddUsersToGroup,
  useFetchGroupUsers,
  useFetchGroups,
  useCurrentUser,
} from "utils/hooks";
import { useTranslation } from "react-i18next";
import { GROUP_USER_ROLES } from "utils/constants";
import {
  useOrganizationSettings,
  getPatientWordPluralCapitalized,
  getPatientWordPlural,
} from "utils/hooks/organizations/useOrganizationSettings";

interface AddMemberFormProps {
  group: Group;
  onSuccess: () => void;
  activeTab: string;
}
interface ApiError {
  email: string;
  message: string;
}
interface AddUsersToGroupResponse {
  success: boolean;
  addedEmails: string[];
  errors: ApiError[];
}

type Role = {
  value: string;
  label: string | undefined;
};

const AddMemberForm = ({ group, onSuccess, activeTab }: AddMemberFormProps) => {
  const { t } = useTranslation(["common"]);
  const { addUsersToGroup } = useAddUsersToGroup(group.uuid);
  const { fetchGroupUsers } = useFetchGroupUsers();
  const { fetchGroups } = useFetchGroups();
  const { user } = useCurrentUser();
  const { settings: orgSettings } = useOrganizationSettings();

  const formMethods = useForm();
  const {
    handleSubmit,
    formState: { isSubmitting },
    control,
    setValue,
  } = formMethods;

  const [selectedPatients, setSelectedPatients] = useState<
    PatientOption[] | null
  >(null);
  const [addedMembers, setAddedMembers] = useState<Member[]>([]);
  const [requiredEmailError, setRequiredEmailError] = useState("");

  const roles = useMemo(
    () => [
      {
        value: GROUP_USER_ROLES.MEMBER,
        label: capitalizeFirstLetter(t("groups.member_label")),
      },
      {
        value: GROUP_USER_ROLES.MODERATOR,
        label: capitalizeFirstLetter(t("groups.moderator_label")),
      },
    ],
    [t]
  );

  const [selectedRole, setSelectedRole] = useState<Role>(roles[0]);
  const [roleHint, setRoleHint] = useState("");
  const [selectValidated, setSelectValidated] = useState(false);

  const isAdding = activeTab === "add";
  const isInviting = activeTab === "invite";
  const isGroupOwner = user?.id === group.groupOwnerId;

  useEffect(() => {
    let hint = t("groups.add_member_panel.role_hint_new_members");
    const type = selectedPatients ? selectedPatients[0].value.type : null;
    const isTherapistIn = selectedPatients?.find(
      (p) => p.value.type === "therapist"
    );
    const mixedTypes = selectedPatients?.find((p) => p.value.type !== type);
    // Set hint for type selector
    if (type === "therapist") {
      hint = t("groups.add_member_panel.role_hint_therapists");
    } else if (type === "patient") {
      hint = t("groups.add_member_panel.role_hint_patients", {
        patientWordCapitalized: getPatientWordPluralCapitalized(orgSettings),
      });
    } else {
      hint = t("groups.add_member_panel.role_hint_new_members");
    }
    setRoleHint(hint);
    // Catch role/permission error and inform user
    if (mixedTypes) {
      toast.warn(
        t("groups.add_member_panel.select_one_type_members_warn", {
          patientWordPlural: getPatientWordPlural(orgSettings),
        })
      );
      setSelectValidated(false);
    } else if (!isGroupOwner && isTherapistIn) {
      toast.warn(t("groups.add_member_panel.role_hint_moderators"));
      setSelectValidated(false);
    } else if (
      selectedRole.value === GROUP_USER_ROLES.MODERATOR &&
      type === "patient"
    ) {
      toast.warn(
        t("groups.add_member_panel.role_hint_patients", {
          patientWordCapitalized: getPatientWordPluralCapitalized(orgSettings),
        })
      );
      setSelectValidated(false);
    } else if (
      selectedRole.value === GROUP_USER_ROLES.MEMBER &&
      type === "therapist"
    ) {
      toast.warn(t("groups.add_member_panel.role_hint_therapists"));
      setSelectValidated(false);
    } else {
      setSelectValidated(true);
    }
  }, [selectedPatients, isGroupOwner, t, roles, selectedRole, orgSettings]);

  const handleUpdateRequiredEmailError = () => {
    setRequiredEmailError("");
  };

  const handleAddPatient = (patients: PatientOption[]) => {
    setSelectedPatients(patients);
  };

  const handleRemovePatient = (removedPatient: PatientOption) => {
    const filteredPatients = (selectedPatients as PatientOption[]).filter(
      (patient: PatientOption) =>
        patient.value.email !== removedPatient.value.email
    );

    const updatedPatients =
      filteredPatients.length > 0 ? filteredPatients : null;

    setSelectedPatients(updatedPatients);
    setValue("patients", updatedPatients);
  };

  const handleAddMemberEmail = (member: Member) => {
    setAddedMembers([...addedMembers, member]);
  };

  const handleRemoveMember = (removedMember: Member) => {
    const updatedEmails = addedMembers.filter(
      (member) => member.email !== removedMember.email
    );

    setAddedMembers(updatedEmails);
  };

  const onSubmit = async (data: any) => {
    if (isInviting && addedMembers.length === 0) {
      setRequiredEmailError(t("groups.add_member_panel.required_member_error"));
      return;
    }
    const formattedData = formatSubmitData(
      { ...data, addedMembers },
      activeTab
    );
    const {
      success,
      addedEmails,
      errors: apiErrors,
    }: AddUsersToGroupResponse = await addUsersToGroup(formattedData);

    if (success) {
      toast(`${pluralize(formattedData.length, "Member")} Added`);
      fetchGroups();
      fetchGroupUsers(group.uuid);
      onSuccess();
    } else if (addedEmails && addedEmails.length > 0) {
      fetchGroups();
      fetchGroupUsers(group.uuid);
      toast.warn(
        `Only the following ${pluralize(
          addedEmails.length,
          "member was",
          "members were"
        )} added: ${addedEmails.join(", ")}`
      );
    }

    if (apiErrors && apiErrors.length > 0 && isInviting) {
      const updatedMembers = formattedData
        .filter((member: Member) => !addedEmails?.includes(member.email))
        .map((member: Member) => {
          const apiError = apiErrors.find(
            (error) => error.email === member.email
          );

          return apiError
            ? {
                ...member,
                error: apiError.message,
              }
            : member;
        });

      setAddedMembers(updatedMembers);
    }
  };

  return (
    <div className="add-member-form">
      <Animate animation="fadeInLeft" delay=".15">
        {isInviting && (
          <InviteNewInput
            addedMembers={addedMembers}
            onAddMemberEmail={handleAddMemberEmail}
            onRemoveMemberEmail={handleRemoveMember}
            requiredEmailError={requiredEmailError}
            onUpdateRequiredEmailError={handleUpdateRequiredEmailError}
          />
        )}
      </Animate>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Animate
          animation="fadeInLeft"
          delay=".15"
          style={{ position: "relative" }}
        >
          {isAdding && (
            <AddExistingSelector
              formMethods={formMethods}
              selectedPatients={selectedPatients}
              onAddPatient={handleAddPatient}
              onRemovePatient={handleRemovePatient}
              groupId={group.uuid}
            />
          )}
        </Animate>
        <Animate animation="fadeInLeft" delay=".25">
          <Select
            name="role"
            label={t("groups.role_label")}
            options={roles}
            value={selectedRole}
            forwardControl={control}
            hint={roleHint}
            defaultValue={roles[0]}
            onChange={(role) => setSelectedRole(role)}
          />
        </Animate>
        <Button
          type="submit"
          name="add_or_invite_member_button"
          value={t(`groups.add_member_panel.${activeTab}_label`)}
          isLoading={isSubmitting}
          extraClass="add-member-form_btn pill thin"
          disabled={
            (isAdding ? !selectedPatients : addedMembers.length === 0) ||
            !selectValidated
          }
        />
      </form>
    </div>
  );
};

export default AddMemberForm;
